import { useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableRow, TextField as MuiTextField, Typography, } from "@mui/material";
import taskerService, {
  type TaskerTask, } from "../../../services/taskerService";
import { readErrorMessage } from "../utils/mapBeErrors";
import { ReviewerActionsFooter } from "./ReviewerActionsFooter";
import { useRemarksMaxLength } from "./useRemarksMaxLength";
import { CharCounter } from "../components/CharCounter";
import { clampText } from "../utils/textLimits";
import { useLatestFormData, useReviewerTaskType } from "./reviewerQueries";
import { stepForms as parseStepForms, transitionForms as parseTransitionForms } from "./taskConfig";
import { deriveCommercial } from "./commercialTerms";
import { PriorReviewerRemarks } from "./PriorReviewerRemarks";
import {
  REVIEWER_APPROVED_MSG, REVIEWER_REJECTED_MSG, REVIEWER_SEND_BACK_MSG_TO_VM, REVIEWER_REMARKS_FIELD, reviewerFormId } from "./reviewerRoles";
import { type OnTransitionSubmitted } from "./ReviewerRouter";
import { bizFinApproveAction } from "./complianceGate";

type Props = { task: TaskerTask; onSubmitted: OnTransitionSubmitted };

type Row = { label: string; value: string | React.ReactNode };

function ContextCard({
  title,
  rows,
}: {
  title: string;
  rows: Row[];
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: "#E0E4EF", borderRadius: "8px", mb: 2 }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "#E0E4EF",
          bgcolor: "#FAFBFF",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
      </Box>
      <Table size="small" sx={{ "& td": { borderColor: "#E0E4EF" } }}>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={`${idx}-${row.label}`}>
              <TableCell
                sx={{ width: "60%", color: "text.secondary", fontSize: 13, py: 1.25 }}
              >
                {row.label}
              </TableCell>
              <TableCell
                sx={{ fontSize: 13, fontWeight: 500, py: 1.25 }}
                align="right"
              >
                {row.value ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export function BizFinancePanel({ task, onSubmitted }: Props) {
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taskTypeQuery = useReviewerTaskType(task.task_type_id);
  const remarksMaxLength = useRemarksMaxLength(
    reviewerFormId(taskTypeQuery.data?.task_config, "bizfin"),
    REVIEWER_REMARKS_FIELD.bizfin,
  );

  const businessDetailsFormId = parseStepForms(
    taskTypeQuery.data?.task_config,
  )?.STEP_1_BUSINESS_DETAILS;

  const vmEnrichmentFormId = parseTransitionForms(
    taskTypeQuery.data?.task_config,
  )?.vm_enrichment;

  const businessDetailsQuery = useLatestFormData(
    "reviewer-business-details",
    task.id,
    businessDetailsFormId,
  );

  const vmEnrichmentQuery = useLatestFormData(
    "reviewer-bizfin-vm-enrichment",
    task.id,
    vmEnrichmentFormId,
  );

  const derivedCommercial = useMemo(() => {
    const d = vmEnrichmentQuery.data ?? {};
    const asString = (v: unknown) =>
      typeof v === "string" && v ? v : undefined;
    return deriveCommercial(
      asString(d.first_tranche_payment_basis),
      asString(d.payment_terms_pattern),
      asString(d.credit_days),
    );
  }, [vmEnrichmentQuery.data]);

  const runTransition = async (
    action:
      | "bizfin_approve"
      | "bizfin_approve_skip"
      | "bizfin_reject"
      | "bizfin_go_back",
    reason?: string,
  ) => {
    if (!task.id) return;
    setSubmitting(true);
    setError(null);
    try {
      const decision =
        action === "bizfin_reject"
          ? "Reject"
          : action === "bizfin_go_back"
          ? "Send Back"
          : "Approve";
      const form_data = {
        biz_fin_decision: decision,
        biz_fin_remarks: reason ?? remarks,
      };
      await taskerService.executeTransition(
        task.id,
        action,
        form_data,
        "reviewer",
      );
      const outcome =
        action === "bizfin_approve" || action === "bizfin_approve_skip"
          ? "approved"
          : "rejected";
      const message =
        action === "bizfin_reject"
          ? REVIEWER_REJECTED_MSG
          : action === "bizfin_go_back"
          ? REVIEWER_SEND_BACK_MSG_TO_VM
          : REVIEWER_APPROVED_MSG.bizfin;
      onSubmitted(outcome, message);
    } catch (err) {
      setError(
        readErrorMessage(err, "Couldn't submit decision. Try again."),
      );
      // Re-throw so reject/send-back callers (the footer's async confirm)
      // see the failure and keep the dialog open with the typed reason.
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => {
    const businessDetailsResolved =
      !businessDetailsQuery.isError && Boolean(businessDetailsFormId);
    const action = bizFinApproveAction(
      businessDetailsQuery.data,
      businessDetailsResolved,
    );
    // Approve doesn't come from a dialog with a reason to preserve — the
    // panel-level error banner (setError above) is enough.
    void runTransition(action).catch(() => {});
  };

  const handleReject = (reason: string) =>
    runTransition("bizfin_reject", reason);

  const handleSendBack = (reason: string) =>
    runTransition("bizfin_go_back", reason);

  const advanceControlRows: Row[] = useMemo(() => {
    const applicable = derivedCommercial.advanceApplicable;
    const percent = derivedCommercial.advancePercent;
    const status =
      applicable === "Yes" ? "Active" : applicable === "No" ? "Inactive" : "—";
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 90);
    const expiryLabel = expiryDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return [
      { label: "Approved advance applicable", value: applicable },
      { label: "Effective advance applicable", value: applicable },
      { label: "Approved advance %", value: percent },
      { label: "Effective advance %", value: percent },
      { label: "Applicability status", value: status },
      { label: "Expiry at (now + 90 days)", value: expiryLabel },
    ];
  }, [derivedCommercial]);

  if (
    taskTypeQuery.isInitialLoading ||
    businessDetailsQuery.isInitialLoading ||
    vmEnrichmentQuery.isInitialLoading
  ) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
        Business Finance review
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
        Commercial-terms sign-off, advance control and operational monitoring.
      </Typography>

      <PriorReviewerRemarks
        task={task}
        taskConfig={taskTypeQuery.data?.task_config}
        authMode="reviewer"
        excludeRole="bizfin"
      />

      {(businessDetailsQuery.isError || !businessDetailsFormId) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn&apos;t resolve Step 1 business details — approve will route
          this task through the Compliance stage regardless of volume.
        </Alert>
      )}

      {(vmEnrichmentQuery.isError || !vmEnrichmentFormId) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn&apos;t resolve the VM-enriched commercial terms — the Advance
          control card below reads &quot;—&quot; because the input is missing,
          not because the VM entered nothing.
        </Alert>
      )}

      <ContextCard title="Advance control" rows={advanceControlRows} />

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
          BizFin remarks
        </Typography>
        <MuiTextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={remarks}
          onChange={(e) => setRemarks(clampText(e.target.value, remarksMaxLength))}
          placeholder="Optional for approve; reject collects a mandatory reason separately."
          inputProps={{ autoComplete: "bizfin-remarks", maxLength: remarksMaxLength }}
          sx={{
            "& .MuiInputBase-input": { fontSize: 13 },
          }}
        />
        <CharCounter length={remarks.length} max={remarksMaxLength} />
      </Box>

      <ReviewerActionsFooter
        reasonMaxLength={remarksMaxLength}
        submitting={submitting}
        errorMessage={error}
        onApprove={handleApprove}
        onReject={handleReject}
        onSendBack={handleSendBack}
        approveLabel="Approve"
        rejectPromptTitle="Reject onboarding"
        sendBackPromptTitle="Send back to Vendor Manager"
        sendBackPromptPlaceholder="Reason to send back (Vendor Manager will see this)"
      />
    </Box>
  );
}

export default BizFinancePanel;
